import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import AuthCard from "../../components/layout/AuthCard";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import { setCredentials, setQuotePermissions, setUserCountry, setUserEmail, setUserStatus } from "../../store/authSlice";
import { loginUser, getUser, getUserFeaturePermissions } from "../../services/api";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState("");

  const validate = () => {
    const errs = {};
    if (!email.trim()) errs.email = "Email address is required.";
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = "Enter a valid email address.";
    if (!password) errs.password = "Password is required.";
    return errs;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setBannerError("");
    setLoading(true);

    try {
      const res = await loginUser({ email, password });
      const { accessToken, refreshToken } = res.data;
      dispatch(setCredentials({ accessToken, refreshToken }));
      dispatch(setUserEmail(email));

      const decoded = JSON.parse(atob(accessToken.split(".")[1]));
      const userId = decoded?.data?.userId;
      const role = decoded?.data?.type; // "sender" | "receiver"

      // Load quote feature permissions before navigation
      try {
        const featRes = await getUserFeaturePermissions(userId);
        const features = featRes?.data?.features || [];
        const quoteFeature = features.find((f) => f.featureName === "QUOTE");
        const subFeatures = quoteFeature?.subFeatures || [];
        const hasWrite = (name) =>
          subFeatures.find((s) => s.featureName === name)?.permissions?.write === true;
        const hasRead = (name) =>
          subFeatures.find((s) => s.featureName === name)?.permissions?.read === true;
        dispatch(setQuotePermissions({
          canOnrampOfframp: hasWrite("CREATE_QUOTE_ONRAMP_OFFRAMP"),
          canOnramp: hasWrite("CREATE_QUOTE_ONRAMP"),
          canOfframp: hasWrite("CREATE_QUOTE_OFFRAMP"),
          canViewTreasury: hasRead("CREATE_QUOTE_ONRAMP"),
        }));
      } catch {
        // permissions will stay at defaults — non-blocking
      }

      const kybEnabled = process.env.REACT_APP_KYB_ENABLED === "true";

      try {
        const userRes = await getUser(userId);
        const status = userRes?.data?.status;
        const country = userRes?.data?.country;
        if (country) dispatch(setUserCountry(country));
        if (status) dispatch(setUserStatus(status));

        if (status === "REJECTED") {
          navigate("/onboarding/kyb/rejected");
        } else if (status === "PENDING") {
          navigate(kybEnabled ? "/onboarding/kyb" : "/onboarding/kyb/pending");
        } else if (role === "receiver") {
          navigate("/receiver/overview");
        } else {
          navigate("/sender/invoices");
        }
      } catch {
        if (role === "receiver") navigate("/receiver/overview");
        else navigate("/sender/invoices");
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 401) {
        setBannerError("Invalid email or password.");
      } else if (msg) {
        setBannerError(msg);
      } else {
        setBannerError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard>
      <h1 className="text-xl font-semibold text-textPrimary mb-1">Sign in to your account</h1>
      {bannerError && (
        <div className="flex items-center gap-2 bg-statusRedBg border border-red-200 text-statusRed rounded-lg px-4 py-3 mb-5 text-sm">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {bannerError}
        </div>
      )}

      <form onSubmit={handleLogin} noValidate>
        <div className="space-y-4">
          <Input
            label="Email address"
            type="email"
            name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); setBannerError(""); }}
            placeholder="you@company.com"
            error={errors.email}
            required
            autoComplete="email"
          />
          <div>
            <Input
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: "" })); setBannerError(""); }}
              placeholder="Enter your password"
              error={errors.password}
              required
              autoComplete="current-password"
              rightElement={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-textMuted hover:text-textPrimary transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              }
            />
            <div className="flex justify-end mt-1.5">
              <Link to="/auth/forgot-password" state={{ email }} className="text-xs text-brand hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <Button type="submit" loading={loading}>Sign in</Button>
        </div>
      </form>
    </AuthCard>
  );
};

export default Login;
